import { useEffect, useRef, useState } from "react"
import api from "../../api/api"
import { dateRange, toDateString } from "../../../utilities/dateUtilities";
import stringHelpers from "../../../utilities/stringHelpers";
import { ManufactureYearBarChart } from "./manufactureYearBarChart";
import { MakeBarChart } from "./makeBarChart";
import { CO2EmissionsChart } from "./co2EmissionsChart";
import { DwellTimeChart } from "./dwellTimeChart";
import { FuelTypePieChart } from "./fuelTypePieChart";
import { VehicleColourPieChart } from "./vehicleColourPieChart";
import { BodyStylePieChart } from "./bodyStylePieChart";
import { saveAs } from 'file-saver';
import arrayUtilities from "../../../utilities/arrayUtilities";
import { FiltersModalTrigger } from "../../shared/filtersModalTrigger";
import { AppliedFilterPill, renderTagFilterItemLabel } from "../../shared/appliedFilterPill";
import appliedFiltersUtilities from "../../../utilities/appliedFiltersUtilities";
import { VehicleAnalyticsFiltersModal } from "./vehicleAnalyticsFiltersModal";
import { PrintPdfHeader } from "../../reports/printPdfHeader";
import { VehicleAnalyticsScheduleModal } from "../../reports/schedule-modals/vehicleAnalyticsScheduleModal";
import { reportDatePresetByLabel } from "../../reports/reportsEnums";

export const VehicleAnalytics = ({ clients, sites, tags, organisationId, organisationName, organisationLogo, token }) => {
    const [analyticsData, setAnalyticsData] = useState(null)
    const [fromDate, setFromDate] = useState(dateRange("currentYear").firstDate);
    const [toDate, setToDate] = useState(dateRange("currentYear").lastDate);
    const [datePreset, setDatePreset] = useState(reportDatePresetByLabel["This year"]);
    const [selectedClient, setSelectedClient] = useState("");
    const [sitesByClient, setSitesByClient] = useState(sites)
    const [selectedSiteByClient, setSelectedSiteByClient] = useState("")
    const [selectedTags, setSelectedTags] = useState([])
    const [fuelTypeBreakdown, setFuelTypeBreakdown] = useState({})
    const [colourBreakdown, setColourBreakdown] = useState({})
    const [bodyStyleBreakdown, setBodyStyleBreakdown] = useState({})
    const [manufacturerBreakdown, setManufacturerBreakdown] = useState({})
    const [manufactureYearBreakdown, setManufactureYearBreakdown] = useState({})
    const [showFiltersModal, setShowFiltersModal] = useState(false);
    const [showFilterPills, setShowFilterPills] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(null);
    const [showScheduleExportModal, setShowScheduleExportModal] = useState(false);

    const scheduleDateParameters = datePreset !== null
        ? { reportDatePreset: datePreset }
        : { from: fromDate, to: toDate }

    const scheduleInitialParameters = {
        ...scheduleDateParameters,
        clientId: selectedClient,
        siteId: selectedSiteByClient,
        tagIds: selectedTags,
    }

    const closeScheduleExportModal = () => setShowScheduleExportModal(false)

    const buildBreakdown = (data, field) => data.reduce((acc, d) => {
        const k = stringHelpers.isNullOrEmpty(d[field]?.toString()) ? "UNKNOWN" : d[field]
        acc[k] = (acc[k] || 0) + 1
        return acc
    }, {})

    const getData = (from, to, siteId, clientId, tagIds) => {
        api.getVehicleAnalyticsData(
            data => setAnalyticsData(data),
            error => console.log(error),
            from, to, !stringHelpers.isNullOrEmpty(siteId) ? siteId : null, !stringHelpers.isNullOrEmpty(clientId) ? clientId : null, 0, tagIds,
            organisationId, token
        )
    }

    useEffect(() => {
        getData(fromDate, toDate, selectedSiteByClient, selectedClient, selectedTags)
        setAppliedFilters(prevState => {
            const updatedFilters = { ...prevState }
            updatedFilters["Date Range"] = `${ toDateString(new Date(fromDate)) } - ${ toDateString(new Date(toDate)) }`
            return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
        })
        setShowFilterPills(true)
    }, [])

    useEffect(() => {
        if (!analyticsData) return
        setFuelTypeBreakdown(buildBreakdown(analyticsData, "fuelType"))
        setColourBreakdown(buildBreakdown(analyticsData, "colour"))
        setBodyStyleBreakdown(buildBreakdown(analyticsData, "bodyStyle"))
        setManufacturerBreakdown(buildBreakdown(analyticsData, "manufacturer"))
        setManufactureYearBreakdown(buildBreakdown(analyticsData, "manufactureYear"))
    }, [analyticsData])

    const reportRef = useRef(null)
    const handlePrintPdf = () => {
        const element = reportRef.current
        if (!element) return

        const barChartSvgs = element.querySelectorAll(
            '.print-col-co2 .recharts-surface, .print-col-manufacture-year .recharts-surface, .print-col-dwell-time .recharts-surface'
        )
        barChartSvgs.forEach(svg => svg.setAttribute('preserveAspectRatio', 'none'))

        const originalTitle = document.title
        document.title = `${organisationName} - Vehicle Analytics`

        element.classList.add("print-target")
        window.print()
        element.classList.remove("print-target")

        document.title = originalTitle

        barChartSvgs.forEach(svg => svg.removeAttribute('preserveAspectRatio'))
    }

    const handleDownload = () => {
        api.getVehicleAnalyticsData(
            (blob, filename) => { saveAs(blob, filename) },
            error => console.log(error),
            fromDate, toDate, !stringHelpers.isNullOrEmpty(selectedSiteByClient) ? selectedSiteByClient : null, !stringHelpers.isNullOrEmpty(selectedClient) ? selectedClient : null, 1, selectedTags,
            organisationId, token
        )
    }

    const resetFilters = () => {
        const defaultRange = dateRange("currentYear")
        setFromDate(defaultRange.firstDate)
        setToDate(defaultRange.lastDate)
        setDatePreset(reportDatePresetByLabel["This year"])
        setSelectedClient("")
        setSelectedSiteByClient("")
        setSitesByClient(sites)
        setSelectedTags([])
        setAppliedFilters({
            "Date Range": `${ toDateString(new Date(defaultRange.firstDate)) } - ${ toDateString(new Date(defaultRange.lastDate)) }`
        })
        setShowFilterPills(true)
        getData(defaultRange.firstDate, defaultRange.lastDate, "", "", [])
    }

    const clearFilterByLabel = (label) => {
        switch (label) {
            case "Client":
                setSelectedClient("")
                setSelectedSiteByClient("")
                setSitesByClient(sites)
                getData(fromDate, toDate, "", "", selectedTags)
                break
            case "Site":
                setSelectedSiteByClient("")
                getData(fromDate, toDate, "", selectedClient, selectedTags)
                break
            case "Date Range": {
                const defaultRange = dateRange("currentYear")
                setFromDate(defaultRange.firstDate)
                setToDate(defaultRange.lastDate)
                setDatePreset(reportDatePresetByLabel["This year"])
                getData(defaultRange.firstDate, defaultRange.lastDate, selectedSiteByClient, selectedClient, selectedTags)
                break
            }
            case "Tags":
                setSelectedTags([])
                getData(fromDate, toDate, selectedSiteByClient, selectedClient, [])
                break
            default:
                break
        }
    }

    const handleFilterPillClose = closedLabel => {
        clearFilterByLabel(closedLabel)
        setAppliedFilters(prevState => {
            const updatedFilters = { ...prevState }
            if (closedLabel === "Date Range") {
                const defaultRange = dateRange("currentYear")
                updatedFilters["Date Range"] = `${ toDateString(new Date(defaultRange.firstDate)) } - ${ toDateString(new Date(defaultRange.lastDate)) }`
            } else if (closedLabel === "Client") {
                updatedFilters.Client = null
                updatedFilters.Site = null
            } else {
                updatedFilters[closedLabel] = null
            }
            return appliedFiltersUtilities.hasRemainingFilters(updatedFilters) ? updatedFilters : null
        })
    }

    const handleFilterPillItemRemove = (label, remainingIds) => {
        setAppliedFilters(prevState => appliedFiltersUtilities.keepMultiSelectionItems(prevState, label, remainingIds))
        removeMultiSelectFilterItemByLabel(label, remainingIds)
    }

    const removeMultiSelectFilterItemByLabel = (label, remainingIds) => {
        switch (label) {
            case "Tags":
                setSelectedTags(remainingIds)
                getData(fromDate, toDate, selectedSiteByClient, selectedClient, remainingIds)
                break
            default:
                break
        }
    }

    const selectedTagNames = !arrayUtilities.isNullOrEmpty(selectedTags) && !arrayUtilities.isNullOrEmpty(tags)
        ? selectedTags
            .map(selectedTagId => tags.find(tag => tag.id === selectedTagId))
            .filter(tag => tag !== undefined)
            .map(tag => tag.name)
            .join(", ")
        : "All"

    if (!analyticsData || !sitesByClient) return null

    return <>
    <div className="container-fluid">
        <div className="row">
            <div className="col-12">
                <div className="card p-3">
                    <div className="d-flex flex-wrap align-items-center justify-content-between">
                        <h5 className="mb-0">Vehicle Analytics</h5>
                        <div className="d-flex flex-wrap align-items-center">
                            <div className="mr-2">
                                <FiltersModalTrigger onClick={ () => setShowFiltersModal(true) } />
                            </div>
                            <button onClick={ () => getData(fromDate, toDate, selectedSiteByClient, selectedClient, selectedTags) } className="btn btn-sm btn-primary nowrap mr-2">
                                Refresh<i className="ml-2 fas fa-redo"></i>
                            </button>
                            <button onClick={ () => handleDownload() } className="btn btn-sm btn-default bg-dark text-white nowrap mr-2">
                                Download CSV<i className="ml-2 far fa-file-excel"></i>
                            </button>
                            <button onClick={ () => handlePrintPdf() } className="btn btn-sm btn-default text-white nowrap" style={ { backgroundColor: "#4A7C8E" } }>
                                Download PDF<i className="ml-2 fas fa-file-pdf"></i>
                            </button>
                            <button
                                onClick={ () => setShowScheduleExportModal(true) }
                                className="btn btn-sm btn-secondary nowrap ml-1"
                            >
                                Schedule Report<i className="ml-2 far fa-clock"></i>
                            </button>
                        </div>
                    </div>
                    {
                        showFilterPills && appliedFilters &&
                        <div className="pt-2 mt-2 border-top d-flex align-items-center flex-wrap">
                            {
                                Object.entries(appliedFilters).map(
                                    ([label, value]) => {
                                        const resettable = label === "Date Range"
                                        return <AppliedFilterPill
                                            key={ label }
                                            label={ label }
                                            value={ value }
                                            isResettable={ resettable }
                                            renderItemLabel={ renderTagFilterItemLabel }
                                            onTextClick={ () => setShowFiltersModal(true) }
                                            onCloseClick={ handleFilterPillClose }
                                            onRemoveItem={ handleFilterPillItemRemove }
                                        />
                                    }
                                )
                            }
                            { appliedFilters && Object.keys(appliedFilters).length > 0 && <button type="button" className="btn btn-none p-0 m-0 fs--1 nowrap" onClick={ () => resetFilters() }>Reset all filters</button> }
                        </div>
                    }
                </div>
                <div ref={ reportRef }>
                <PrintPdfHeader organisationLogo={ organisationLogo } organisationName={ organisationName } title="Vehicle Analytics">
                    <div className="print-only-filter">
                        <span className="print-only-filter-label">Date Range</span>
                        <span className="print-only-filter-value">
                            {toDateString(fromDate)} - {toDateString(toDate)}
                        </span>
                    </div>
                    <div className="print-only-filter">
                        <span className="print-only-filter-label">Client</span>
                        <span className="print-only-filter-value">
                            {
                                stringHelpers.isNullOrEmpty(selectedClient)
                                ? <>All</>
                                : clients.find(item => item.id === selectedClient).name
                            }
                        </span>
                    </div>
                    <div className="print-only-filter">
                        <span className="print-only-filter-label">Site</span>
                        <span className="print-only-filter-value">
                            {
                                stringHelpers.isNullOrEmpty(selectedSiteByClient)
                                ? <>All</>
                                : sitesByClient.find(item => item.id === selectedSiteByClient).name
                            }
                        </span>
                    </div>
                    {
                        tags &&
                        <div className="print-only-filter">
                            <span className="print-only-filter-label">Tags</span>
                            <span className="print-only-filter-value">{ selectedTagNames }</span>
                        </div>
                    }
                </PrintPdfHeader>
                <div className="card mt-3 mb-3">
                    <div className="d-flex flex-wrap">
                        { [
                            { label: "Total Visits", value: stringHelpers.formatDecimal(analyticsData.length, 0) },
                            { label: "Unique VRMs", value: stringHelpers.formatDecimal([...new Set(analyticsData.map(d => d.vrm))].length, 0) },
                            (() => { const co2Data = analyticsData.filter(d => d.co2EmissionsGKm !== 0 && d.co2EmissionsGKm !== 999); return { label: "Avg. CO2 Emissions", value: co2Data.length ? `${stringHelpers.formatDecimal(co2Data.reduce((a, b) => a + b.co2EmissionsGKm, 0) / co2Data.length, 2)} g/km` : "—" } })(),
                            { label: "Avg. Dwell Time", value: analyticsData.length ? `${stringHelpers.formatDecimal(analyticsData.reduce((a, b) => a + b.dwellTimeMinutes, 0) / analyticsData.length, 2)} mins` : "—" },
                            { label: "Avg. Engine Power", value: analyticsData.length ? `${stringHelpers.formatDecimal(analyticsData.reduce((a, b) => a + b.enginePowerKw, 0) / analyticsData.length, 2)} kW` : "—" },
                            { label: "Avg. Gross Weight", value: analyticsData.length ? `${stringHelpers.formatDecimal(analyticsData.reduce((a, b) => a + b.grossWeightKg, 0) / analyticsData.length, 2)} kg` : "—" },
                        ].map((stat, i, arr) => (
                            <div key={ stat.label } className="d-flex flex-column align-items-center analytics-stat-item" style={ { borderRight: i < arr.length - 1 ? "1px solid #f0f4f8" : "none" } }>
                                <b className="text-uppercase fs--2 analytics-section-label">{ stat.label }</b>
                                <b className="m-0 fs-1 text-dark">{ stat.value }</b>
                            </div>
                        )) }
                    </div>
                </div>
                { arrayUtilities.isNullOrEmpty(analyticsData) 
                ? (
                    <div className="card mt-3 mb-3 d-flex align-items-center justify-content-center analytics-empty-state">
                        <div className="d-flex flex-column align-items-center">
                            <i className="fas fa-chart-bar fs-3"></i>
                            <p className="m-0 fs-1">No data available</p>
                            <p className="fs--1">Try adjusting the date range or filters</p>
                        </div>
                    </div>
                ) 
                : <div className="print-analytics-grid">
                    <div className="row align-items-stretch">
                        <div className="col-12 col-md-4 mb-3 d-flex flex-column print-col-fuel-type">
                            <FuelTypePieChart breakdown={ fuelTypeBreakdown } />
                        </div>
                        <div className="col-12 col-md-8 mb-3 d-flex flex-column print-col-co2">
                            <CO2EmissionsChart analyticsData={ analyticsData } />
                        </div>
                    </div>
                    <div className="row align-items-stretch">
                        <div className="col-12 col-md-3 mb-3 d-flex flex-column print-col-colour">
                            <VehicleColourPieChart breakdown={ colourBreakdown } />
                        </div>
                        <div className="col-12 col-md-3 mb-3 d-flex flex-column print-col-body-style">
                            <BodyStylePieChart breakdown={ bodyStyleBreakdown } />
                        </div>
                        <div className="col-12 col-md-6 mb-3 d-flex flex-column print-col-manufacture-year">
                            <ManufactureYearBarChart breakdown={ manufactureYearBreakdown } />
                        </div>
                    </div>
                    <div className="row align-items-stretch">
                        <div className="col-12 col-md-6 mb-3 d-flex flex-column print-col-make">
                            <MakeBarChart breakdown={ manufacturerBreakdown } />
                        </div>
                        <div className="col-12 col-md-6 mb-3 d-flex flex-column print-col-dwell-time">
                            <DwellTimeChart analyticsData={ analyticsData } />
                        </div>
                    </div>
                </div> }
                </div>
            </div>
        </div>

        {
            showScheduleExportModal &&
            <VehicleAnalyticsScheduleModal
                sites={ sites }
                clients={ clients }
                tags={ tags || [] }
                organisationId={ organisationId }
                token={ token }
                initialParameters={ scheduleInitialParameters }
                initialSitesByClient={ sitesByClient }
                onCancel={ closeScheduleExportModal }
                onSaved={ closeScheduleExportModal }
            />
        }
    </div>
    {
        showFiltersModal &&
        <VehicleAnalyticsFiltersModal
            showModal={ showFiltersModal }
            handleClose={ () => setShowFiltersModal(false) }
            handleConfirm={ (draftFilters) => {
                setFromDate(draftFilters.fromDate)
                setToDate(draftFilters.toDate)
                setDatePreset(draftFilters.datePreset)
                setSelectedClient(draftFilters.clientId)
                setSelectedSiteByClient(draftFilters.siteId)
                setSitesByClient(draftFilters.sitesByClient)
                setSelectedTags(draftFilters.selectedTags)
                setAppliedFilters(draftFilters.appliedFilters)
                setShowFilterPills(true)
                setShowFiltersModal(false)
                getData(draftFilters.fromDate, draftFilters.toDate, draftFilters.siteId, draftFilters.clientId, draftFilters.selectedTags)
            } }
            initialFromDate={ fromDate }
            initialToDate={ toDate }
            initialDatePreset={ datePreset }
            initialClientId={ selectedClient }
            initialSiteId={ selectedSiteByClient }
            initialSitesByClient={ sitesByClient }
            initialSelectedTags={ selectedTags }
            initialAppliedFilters={ appliedFilters }
            clients={ clients }
            sites={ sites }
            tags={ tags }
            organisationId={ organisationId }
            token={ token }
            key="vehicle-analytics-filters-modal"
        />
    }
    </>
}